import { Type } from '@sinclair/typebox';
// M24 — DB-connector semantic-layer spec contract (HUB-079 §3/§4; DBC-02/03/04).
// This is the `schema_json` jsonb stored per connector schema version: LLM-generated
// from DBML + BI prompt, hand-editable afterwards, validated on every save.
// `active` absent = active (checkbox curation, DBC-04).
export const DbConnectorDimensionTypeSchema = Type.Union([
    Type.Literal('string'),
    Type.Literal('number'),
    Type.Literal('date'),
    Type.Literal('datetime'),
    Type.Literal('boolean'),
    Type.Literal('time'),
]);
export const DbConnectorMetricAggSchema = Type.Union([
    Type.Literal('count'),
    Type.Literal('sum'),
    Type.Literal('avg'),
    Type.Literal('min'),
    Type.Literal('max'),
]);
export const DbConnectorSpecDimensionSchema = Type.Object({
    name: Type.String(),
    column: Type.String(),
    type: DbConnectorDimensionTypeSchema,
    primaryKey: Type.Optional(Type.Boolean()),
    description: Type.Optional(Type.String()),
    active: Type.Optional(Type.Boolean()),
});
export const DbConnectorSpecMetricSchema = Type.Object({
    name: Type.String(),
    column: Type.String(),
    agg: DbConnectorMetricAggSchema,
    description: Type.Optional(Type.String()),
    active: Type.Optional(Type.Boolean()),
});
export const DbConnectorSpecHierarchySchema = Type.Object({
    name: Type.String(),
    elements: Type.Array(Type.Object({
        name: Type.String(),
        dimensions: Type.Array(Type.String()),
    })),
    active: Type.Optional(Type.Boolean()),
});
export const DbConnectorSpecModelSchema = Type.Object({
    /** Sanitized model name: [a-zA-Z0-9_]. */
    name: Type.String(),
    schema: Type.String(),
    table: Type.String(),
    description: Type.Optional(Type.String()),
    active: Type.Optional(Type.Boolean()),
    dimensions: Type.Array(DbConnectorSpecDimensionSchema),
    metrics: Type.Array(DbConnectorSpecMetricSchema),
    categoricalHierarchies: Type.Optional(Type.Array(DbConnectorSpecHierarchySchema)),
});
export const DbConnectorSpecJoinSchema = Type.Object({
    type: Type.Union([Type.Literal('oneToOne'), Type.Literal('manyToOne')]),
    from: Type.String(),
    to: Type.String(),
    on: Type.Array(Type.Object({
        fromColumn: Type.String(),
        toColumn: Type.String(),
    })),
});
export const DbConnectorSpecSchema = Type.Object({
    version: Type.Literal(1),
    models: Type.Array(DbConnectorSpecModelSchema),
    joins: Type.Array(DbConnectorSpecJoinSchema),
});
// --- /db-connectors route contracts (M24 task-05; HUB-079 §5) ---
// Connection-string material (plaintext OR ciphertext) is deliberately ABSENT
// from every response schema — it never leaves the DB row.
export const DbConnectorEngineSchema = Type.Union([
    Type.Literal('postgresql'),
    Type.Literal('mssql'),
]);
export const DbConnectorStatusSchema = Type.Union([
    Type.Literal('active'),
    Type.Literal('unreachable'),
]);
export const DbConnectorSchemaStatusSchema = Type.Union([
    Type.Literal('pending'),
    Type.Literal('generating'),
    Type.Literal('ready'),
    Type.Literal('error'),
]);
/** List-item shape shared by GET /, POST / (201) and PATCH /:id responses. */
export const DbConnectorItemSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    engine: DbConnectorEngineSchema,
    status: DbConnectorStatusSchema,
    schemaStatus: DbConnectorSchemaStatusSchema,
    allowSampling: Type.Boolean(),
    biPrompt: Type.Union([Type.String(), Type.Null()]),
    queryPrompt: Type.Union([Type.String(), Type.Null()]),
    activeSchemaVersion: Type.Union([Type.Integer(), Type.Null()]),
    lastSyncedAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    lastError: Type.Union([Type.String(), Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
});
export const DbConnectorListResponseSchema = Type.Object({
    connectors: Type.Array(DbConnectorItemSchema),
});
export const DbConnectorIdParamsSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
});
export const DbConnectorCreateBodySchema = Type.Object({
    name: Type.String({ minLength: 1 }),
    engine: DbConnectorEngineSchema,
    connectionString: Type.String({ minLength: 1 }),
    biPrompt: Type.Optional(Type.String()),
    queryPrompt: Type.Optional(Type.String()),
});
export const DbConnectorTestResponseSchema = Type.Object({
    ok: Type.Boolean(),
    status: DbConnectorStatusSchema,
    error: Type.Optional(Type.String()),
});
/** 202 body of POST /db-connectors/:id/sync — the route never awaits the sync. */
export const DbConnectorSyncQueuedResponseSchema = Type.Object({
    queued: Type.Literal(true),
});
/** A stored schema version (presets are named versions). */
export const DbConnectorSchemaVersionSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    version: Type.Integer(),
    name: Type.Union([Type.String(), Type.Null()]),
    generatedBy: Type.Union([Type.Literal('llm'), Type.Literal('manual')]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
/** The active version enriched with its spec (GET /:id/schema `active`, PUT response). */
export const DbConnectorActiveSchemaSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    version: Type.Integer(),
    name: Type.Union([Type.String(), Type.Null()]),
    generatedBy: Type.Union([Type.Literal('llm'), Type.Literal('manual')]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
    spec: DbConnectorSpecSchema,
});
export const DbConnectorSchemaResponseSchema = Type.Object({
    active: Type.Union([DbConnectorActiveSchemaSchema, Type.Null()]),
    versions: Type.Array(DbConnectorSchemaVersionSchema),
});
/** Raw-editor save. `spec` is validated server-side (422 invalid_spec + issues). */
export const DbConnectorSchemaSaveBodySchema = Type.Object({
    spec: Type.Unknown(),
    saveAs: Type.Optional(Type.String({ minLength: 1 })),
});
export const DbConnectorInvalidSpecResponseSchema = Type.Object({
    error: Type.Literal('invalid_spec'),
    issues: Type.Array(Type.String()),
});
export const DbConnectorActivateBodySchema = Type.Object({
    version: Type.Integer(),
});
export const DbConnectorActivateResponseSchema = Type.Object({
    activeSchemaVersion: Type.Integer(),
});
export const DbConnectorUpdateBodySchema = Type.Object({
    name: Type.Optional(Type.String({ minLength: 1 })),
    connectionString: Type.Optional(Type.String({ minLength: 1 })),
    biPrompt: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    queryPrompt: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    allowSampling: Type.Optional(Type.Boolean()),
});
// --- `data-db-table` stream/persisted part payload (M24 task-07; COP-114 §5, DBC-07) ---
// One part per executed lane query. Rows are display-capped server-side at
// `partRowCap`; `chart.available` is computed SERVER-side from the semantic
// layer's query analysis (the client never loads semantic-layer).
export const DbTableChartTypeSchema = Type.Union([
    Type.Literal('bar'),
    Type.Literal('pareto'),
    Type.Literal('groupedBar'),
    Type.Literal('line'),
]);
export const DbTablePartColumnSchema = Type.Object({
    /** Result alias: member path with '.' → '___'. */
    key: Type.String(),
    /** Semantic-layer member path (e.g. `customers.country`). */
    path: Type.String(),
    type: Type.String(),
    memberType: Type.Union([Type.Literal('dimension'), Type.Literal('metric')]),
});
export const DbTablePartDataSchema = Type.Object({
    /** Minted per part (uuid). */
    id: Type.String({ format: 'uuid' }),
    connectorId: Type.String({ format: 'uuid' }),
    connectorName: Type.String(),
    title: Type.String(),
    /** The parsed semantic-layer query spec that produced the rows. */
    query: Type.Unknown(),
    columns: Type.Array(DbTablePartColumnSchema),
    /** Display rows, capped at `partRowCap` (default 100). */
    rows: Type.Array(Type.Record(Type.String(), Type.Unknown())),
    totalRowCount: Type.Integer(),
    truncated: Type.Boolean(),
    chart: Type.Union([
        Type.Object({ available: Type.Array(DbTableChartTypeSchema) }),
        Type.Null(),
    ]),
    /** ISO timestamp of query execution. */
    executedAt: Type.String(),
});
//# sourceMappingURL=db-connectors.js.map